import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from './api.service';
import { Brand, SimpleUser } from '../interfaces';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AnalyticsService } from './analytics.service';
import { EmitterService } from './emitter.service';
import { BrandUpdatedEvent, UserLoggedOutEvent } from '../models/events';
import { FeatureConfigService } from './feature-config.service';
import { ApiResponseMessages } from '../constants/users-and-teams.constants';
import { EntityParticipant, EntityParticipantsService } from './entity-participants.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';

@Injectable({
  providedIn: 'root',
})
export class BrandService {
  private readonly authService = inject(AuthService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly featureConfigService = inject(FeatureConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entityParticipants: EntityParticipantsService = inject(EntityParticipantsService);

  private readonly _brands: WritableSignal<Brand[]> = signal<Brand[]>([]);
  private readonly _activeBrand: WritableSignal<Brand | null> = signal<Brand | null>(this.authService.getUserBrand());
  private brandsRequested: boolean = false;

  readonly brands: Signal<Brand[]> = this._brands.asReadonly();
  readonly activeBrand: Signal<Brand | null> = this._activeBrand.asReadonly();
  readonly canSwitchBrand: Signal<boolean> = computed(() => this._brands().length > 1);

  constructor(private readonly api: ApiService) {
    // localStorage is the app's real "current brand" and several pages write it directly via
    // setMyBrand() (dashboard on every load, task page, the legacy per-page dropdowns). Follow the
    // event so the signal-driven UI (breadcrumb dropdown, sidebar) can never show a different
    // brand than the one the page data is built from.
    EmitterService.get(BrandUpdatedEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brand: Brand | null) => {
        if (brand?.id && brand.id !== this._activeBrand()?.id) {
          this._activeBrand.set(brand);
        }
      });

    // The root instance outlives logout/login (SPA navigation, no reload): without a reset the
    // next user sees the previous user's brand list, and the brandsRequested latch blocks
    // loadUserBrands() from ever fetching theirs. Same idiom as EntityParticipantsService.
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._brands.set([]);
        this._activeBrand.set(null);
        this.brandsRequested = false;
      });
  }

  /**
   * Loads the user's brands once per session. Safe to call from any consumer (breadcrumb,
   * sidebar, shell): repeat calls are no-ops, and the public-link/anonymous guards live here
   * rather than at the call sites so no host can trigger the request on a review/share link.
   * Also persists the active brand so it is available before visiting a brand-aware page.
   */
  public loadUserBrands(): void {
    if (this.brandsRequested || !this.canLoadBrands()) {
      return;
    }
    this.brandsRequested = true;
    this.analyticsService.getBrands().subscribe({
      next: (response: Brand[]) => {
        const brands: Brand[] = Array.isArray(response) ? response : [];
        this._brands.set(brands);
        const active: Brand | null = this.resolveActiveBrand(brands);
        if (active) {
          this.authService.setMyBrand(active);
          this._activeBrand.set(active);
        }
      },
      error: () => {
        // Allow a later consumer to retry (e.g. the request was cancelled mid-navigation).
        this.brandsRequested = false;
      },
    });
  }

  /** Authenticated in-app users only — public share links must not request it. */
  private canLoadBrands(): boolean {
    return !!this.authService.getAuthToken() && !!this.authService.getAuthUser();
  }

  /**
   * Full switch sequence: persist the brand (patches auth_user.brandId, clears brand-scoped
   * storage, emits BrandUpdatedEvent), drop the feature-config cache, then resolve the initial
   * route for the NEW brand and navigate with a full document load.
   */
  public switchBrand(brand: Brand): void {
    if (!brand?.id || brand.id === this.authService.getCurrentBrandId()) {
      return;
    }
    this.authService.setMyBrand(brand);
    this._activeBrand.set(brand);
    this.featureConfigService.clearFeatureConfig();
    this.featureConfigService.getInitialRoute().subscribe((route: string[]) => {
      this.navigateToUrl(route.join('/'));
    });
  }

  /**
   * Precedence must match the pages that also resolve a brand on load (workspace dashboard):
   * the brand the user was last in, then the user's stored selection, then the first brand
   * returned by the endpoint. The default brand (Account Preferences choice, `auth_user.brandId`)
   * is retired and no longer consulted — with no last-selected brand the user lands on brands[0].
   * Every candidate is matched against the returned list, so a brand the user has lost access to
   * falls through to the next one instead of resolving to nothing.
   * Diverging here is what made the menus show brands[0] while the page data used the default.
   */
  private resolveActiveBrand(brands: Brand[]): Brand | null {
    const lastVisitedId: string | null = this.authService.getLastVisitedBrandId();
    const storedId: string = this.authService.getCurrentBrandId();
    return (
      brands.find((b: Brand) => !!lastVisitedId && b.id === lastVisitedId) ??
      brands.find((b: Brand) => !!storedId && b.id === storedId) ??
      brands[0] ??
      null
    );
  }

  /** Full document load — same nuke-everything semantics as the pre-existing reload-based switch. */
  protected navigateToUrl(url: string): void {
    window.location.href = url;
  }

  /**
   * The loaded brand that owns `workspaceId`, or null when none does.
   *
   * `Brand.workspaces` is the id list `GET /api/brands` already returns, and
   * {@link loadUserBrands} holds every brand the user has — so this answers
   * workspace → brand with no request and nothing to invalidate. Null while the list
   * has not loaded yet, so callers must treat it as "cannot tell", not "no brand".
   */
  public brandForWorkspace(workspaceId: string): Brand | null {
    if (!workspaceId) {
      return null;
    }
    return this._brands().find((brand: Brand) => brand.workspaces?.includes(workspaceId)) ?? null;
  }

  public getBrandsByCompanyId(companyId: string): Observable<Brand[]> {
    return this.api.httpGet(`/api/brands/${companyId}`);
  }

  public getBrandsByBrandId(companyId: string, brandId: string): Observable<Brand> {
    return this.api.httpGet(`/api/brands/${companyId}/${brandId}`);
  }

  public createBrand(brandName: string): Observable<Brand> {
    return this.api.httpPost('/api/brands', { name: brandName });
  }

  public updateBrand(brand: Partial<Brand>): Observable<Brand> {
    return this.api.httpPost(`/api/brands/${brand.id}`, brand);
  }

  public addWorkspacesToBrand(companyId: string, brandId: string, workspaceIds: string[]): Observable<Brand> {
    return this.api.httpPost(`/api/brands/${companyId}/${brandId}/add-workspaces`, workspaceIds);
  }

  /**
   * No-op: POST /api/brands/{companyId}/{brandId}/add-users is retired (RBAC permission
   * migration). Brand user assignment is managed outside this app; the OK stub keeps the
   * calling UI flows intact.
   */
  public addUsersToBrand(_companyId: string, _brandId: string, _users: { username: string }[]): Observable<any> {
    return of({ message: ApiResponseMessages.OK });
  }

  /**
   * Brand users via the unified per-entity participants endpoint (same as the task surfaces);
   * the /api/brands/{brandId}/users listings — bare and role-filtered — are retired. Rows are
   * a partial SimpleUser shape (username/name/email/avatar — no role data).
   */
  public getBrandUsers(brandId: string): Observable<SimpleUser[]> {
    return this.entityParticipants
      .getParticipants(PLATFORM_CHAT_ENTITY_TYPE.BRAND, brandId)
      .pipe(map((participants: EntityParticipant[]) => participants as unknown as SimpleUser[]));
  }
}
